import React, { useMemo } from 'react';
import { useAgencyDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/store/agencyDashboard.store';
import { useChartColors } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { HeatMapGrid } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/HeatMapGrid';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import {
  TIMEFRAMES,
  DAYS_OF_WEEK,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.constants';

function buildHeatMapMatrix(
  cells: { timeframe: string; dayOfWeek: string; shiftCount: number }[],
  rowLabels: readonly string[],
  colLabels: readonly string[],
): number[][] {
  const lookup = new Map(cells.map((c) => [`${c.timeframe}-${c.dayOfWeek}`, c.shiftCount]));
  return rowLabels.map((row) => colLabels.map((col) => lookup.get(`${row}-${col}`) ?? 0));
}

export const HardestTimeframesChart: React.FC = () => {
  const data = useAgencyDashboardStore((s) => s.shiftsByTimeframe);
  const palette = useChartColors();

  const matrix = useMemo(
    () => (data ? buildHeatMapMatrix(data, TIMEFRAMES, DAYS_OF_WEEK) : []),
    [data],
  );

  const colorScale = useMemo(() => {
    const base = palette.chartColors[3] || '#1C0F51';
    const r = parseInt(base.slice(1, 3), 16);
    const g = parseInt(base.slice(3, 5), 16);
    const b = parseInt(base.slice(5, 7), 16);
    return (value: number, max: number) => {
      if (value === 0) return '#F5F5F5';
      const t = Math.max(0.2, value / max);
      const mix = (c: number) => Math.round(255 - (255 - c) * t);
      return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
    };
  }, [palette.chartColors]);

  if (!data || data.length === 0) {
    return (
      <EmptyStateContainer>
        No agency timeframe data available for the selected filters.
      </EmptyStateContainer>
    );
  }

  return (
    <HeatMapGrid
      rowLabels={[...TIMEFRAMES]}
      columnLabels={[...DAYS_OF_WEEK]}
      data={matrix}
      colorScale={colorScale}
      valueFormatter={(v) => `${v} shifts`}
      ariaLabel="Hardest timeframes to fill internally heat map showing agency shifts by day and time of day"
    />
  );
};
